import * as React from "react"
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop"
import Chart from 'react-apexcharts'
import dayjs from "dayjs"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { useSnackbar } from "notistack"

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
}).format

export default function CashflowReport({ startDatetime, endDatetime, selectedLocations }) {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = React.useState(true)
    const [cashflowData, setCashflowData] = React.useState([])
    const [selectedStep, setSelectedStep] = React.useState(7)

    const getCashflow = (start_datetime, end_datetime, location_ids) => {
        if (!location_ids || location_ids.length === 0) {
            return
        }
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("reports/cashflow", {
                params: {
                    start_datetime: start_datetime,
                    end_datetime: end_datetime.add(1, 'day'),
                    location_ids: location_ids
                },
            }).then((cashflow) => {
                setCashflowData(cashflow.data.data)
                setIsLoading(false)
            }).catch((error) => {
                console.log(error)
                setIsLoading(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        getCashflow(startDatetime, endDatetime, selectedLocations.map((x) => x.id))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDatetime, endDatetime, selectedLocations])

    const days = []
    for (let i = startDatetime; i <= endDatetime; i = i.add(1, 'day')) {
        days.push(i.startOf('day'))
    }

    const series = []
    const found_payment_types = {}
    cashflowData.forEach((x) => found_payment_types[x.payment_type] = 0)

    Object.keys(found_payment_types).sort().forEach((payment_type) => {
        let data = days.map((day) => [day, 0])
        cashflowData.filter((x) => x.payment_type === payment_type).forEach((x) => {
            const day_diff = dayjs.utc(x.date_created).diff(days[0], 'days')
            if (day_diff >= 0 && day_diff < data.length) {
                const entry = data[day_diff]
                entry[1] = (entry[1] ?? 0) + x.total_cost
            }
        })
        if (selectedStep > 1) {
            let new_data = []
            for (let i = 0; i < data.length; i += selectedStep) {
                const val = data.slice(i, Math.min(i + selectedStep, data.length)).reduce((partialSum, a) => partialSum + a[1] ?? 0, 0)
                new_data.push([data[i][0], val])
            }
            data = new_data
        }
        series.push({
            name: payment_type,
            data: data
        })
    })

    const table_data = series.length === 0 ? [] : series[0].data.map((data) => { return { id: data[0].valueOf(), date: data[0] } })
    series.forEach((each) => {
        each.data.forEach((val, index) => {
            table_data[index][each.name] = val[1]
        })
    })

    return (
        <React.Fragment>
            <Box>
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text="Loading Cashflow Report"
                />
                {!isLoading && cashflowData.length === 0 &&
                    <h3>NO DATA</h3>
                }
                {!isLoading && cashflowData.length > 0 &&
                    <Box>
                        <p>Payments made over time</p>
                        <FormControl>
                            <InputLabel id="kpis-group-time-category">Step</InputLabel>
                            <Select
                                labelId="kpis-group-time-category"
                                label="Step"
                                onChange={(event) => {
                                    setSelectedStep(event.target.value)
                                }}
                                value={selectedStep}
                                sx={{ width: '115px' }}
                            >
                                {[1, 7, 30, 60, 90].map((n) =>
                                    <MenuItem key={`${n}`} value={n}>
                                        {`${n} Day${n > 1 ? 's' : ''}`}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Chart
                            type="area" height={450}
                            options={{
                                chart: {
                                    height: 450,
                                    type: 'area',
                                    stacked: true,
                                    width: '100%',
                                    zoom: {
                                        enabled: true
                                    }
                                },
                                plotOptions: {
                                    area: {
                                        fillTo: 'origin',
                                    }
                                },
                                dataLabels: {
                                    enabled: false
                                },
                                stroke: {
                                    curve: "monotoneCubic"
                                },
                                fill: {
                                    type: 'gradient',
                                    gradient: {
                                        opacityFrom: 0.6,
                                        opacityTo: 0.8,
                                    }
                                },
                                xaxis: {
                                    type: 'datetime',
                                    tooltip: {
                                        enabled: false
                                    }
                                },
                                yaxis: {
                                    decimalsInFloat: 0,
                                    title: {
                                        text: 'Dollars'
                                    }
                                },
                                tooltip: {
                                    y: {
                                        formatter: (val) => currencyFormatter(val),
                                    },
                                    x: {
                                        show: true,
                                        formatter: selectedStep > 1 ? (thing) => `${dayjs.utc(thing).format('M/D/YYYY')} - ${dayjs.utc(thing).add(selectedStep - 1, 'day').format('M/D/YYYY')}` : undefined,
                                    }
                                }
                            }}
                            series={series}
                        />
                        <DataGrid
                            rows={table_data}
                            slots={{ toolbar: GridToolbar }}
                            columns={[
                                {
                                    field: "date",
                                    headerName: "Date",
                                    flex: 1,
                                    editable: false,
                                    sortable: true,
                                    valueFormatter: (value) => selectedStep > 1 ? `${value.format('M/D/YYYY')} - ${value.add(selectedStep - 1, 'day').format('M/D/YYYY')}` : `${value.format('M/D/YYYY')}`
                                },
                                ...Object.keys(found_payment_types).map((type) => {
                                    return {
                                        field: type,
                                        headerName: type,
                                        flex: 1,
                                        editable: false,
                                        sortable: true,
                                        valueFormatter: currencyFormatter
                                    }
                                })
                            ]}
                            autoHeight={true}
                            pagination={true}
                            pageSize={100}
                            disableRowSelectionOnClick
                        />
                    </Box>
                }
            </Box>
        </React.Fragment>
    )
}